import Swiper from 'swiper';
import { bindDialog } from './dialog.mjs';

/** A read-only view of an addon; all purchase state stays in the original form. */
export const initializeAddonPreview = (row, config, getVariantId, lockScroll) => {
    const doc = row.ownerDocument;
    const win = doc.defaultView;
    const template = row.querySelector('[data-addon-preview-template]');
    if (!win.HTMLDialogElement?.prototype.showModal || !template?.content.firstElementChild || !lockScroll) return;
    const triggers = [...row.querySelectorAll('[data-addon-preview]')];
    const id = template.content.firstElementChild.id;
    let dialog;
    let activeTrigger;
    let unlock;
    let selectPhoto;

    const prepare = () => {
        dialog = template.content.firstElementChild.cloneNode(true);
        doc.body.append(dialog);
        const controller = bindDialog(dialog, [], { closeAnimation: 'addon-preview-exit' });
        const photos = [...dialog.querySelectorAll('[data-preview-photo]')];
        const slides = [...dialog.querySelectorAll('[data-preview-slide]')];
        const thumbnails = [...dialog.querySelectorAll('[data-preview-index]')];
        const counter = dialog.querySelector('[data-preview-counter]');
        const reducedMotion = win.matchMedia('(prefers-reduced-motion: reduce)');
        const transitionSpeed = () => reducedMotion.matches || !doc.body.classList.contains('ovalia-motion') ? 0 : 360;
        let index = 0;
        const sync = (slider) => {
            index = slider.activeIndex;
            slides.forEach((slide, position) => slide.setAttribute('aria-hidden', String(position !== index)));
            photos.forEach((photo, position) => {
                if (position === index) photo.loading = 'eager';
            });
            thumbnails.forEach((button, position) => {
                button.setAttribute('aria-pressed', String(position === index));
                button.tabIndex = position === index ? 0 : -1;
            });
            if (counter) {
                counter.textContent = `${index + 1} / ${photos.length}`;
                counter.setAttribute('aria-label', config.photo.replace('{current}', index + 1).replace('{total}', photos.length));
            }
            const selected = thumbnails[index];
            if (selected && dialog.open) {
                const rail = selected.parentElement;
                const box = selected.getBoundingClientRect();
                const bounds = rail.getBoundingClientRect();
                const overflow = box.left < bounds.left ? box.left - bounds.left : Math.max(0, box.right - bounds.right);
                if (overflow) rail.scrollLeft += overflow;
            }
        };
        slides.forEach(slide => { slide.hidden = false; });
        const slider = photos.length ? new Swiper(dialog.querySelector('[data-preview-viewport]'), {
            // Measure after showModal(); a closed dialog has no usable dimensions.
            init: false,
            slidesPerView: 1,
            speed: transitionSpeed(),
            rewind: true,
            watchOverflow: true,
            allowTouchMove: photos.length > 1,
            simulateTouch: true,
            grabCursor: photos.length > 1,
            threshold: 6,
            touchAngle: 40,
            longSwipesRatio: 0.2,
            touchStartPreventDefault: false,
            preventClicks: true,
            preventClicksPropagation: true,
            lazyPreloadPrevNext: 1,
            on: { init: sync, slideChange: sync },
        }) : null;
        if (slider) {
            const updateMotion = () => { slider.params.speed = transitionSpeed(); };
            reducedMotion.addEventListener('change', updateMotion);
            slider.on('destroy', () => reducedMotion.removeEventListener('change', updateMotion));
        }
        selectPhoto = (next, speed) => {
            if (!slider) return;
            if (!slider.initialized) slider.init();
            else if (speed === 0) slider.update();
            slider.slideTo((next + photos.length) % photos.length, speed ?? slider.params.speed);
            sync(slider);
        };
        thumbnails.forEach((button, position) => button.addEventListener('click', () => selectPhoto(position)));
        dialog.querySelectorAll('[data-preview-step]').forEach(button => button.addEventListener('click', () => selectPhoto(index + Number(button.dataset.previewStep))));
        dialog.querySelector('[data-preview-gallery]').addEventListener('keydown', event => {
            if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
            const destinations = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: photos.length - 1 };
            if (!(event.key in destinations)) return;
            event.preventDefault();
            selectPhoto(destinations[event.key]);
            if (event.target.matches('[data-preview-index]')) thumbnails[index]?.focus({ preventScroll: true });
        });
        let outside = false;
        const isBackdrop = event => {
            const box = dialog.getBoundingClientRect();
            return event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom);
        };
        dialog.addEventListener('pointerdown', event => { outside = isBackdrop(event); });
        dialog.addEventListener('pointercancel', () => { outside = false; });
        dialog.addEventListener('click', event => { if (outside && isBackdrop(event)) controller.close(); outside = false; });
        dialog.addEventListener('close', () => {
            unlock?.(); unlock = null;
            triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
            activeTrigger?.focus({ preventScroll: true });
        });
        win.addEventListener('pageshow', event => { if (event.persisted && dialog.open) dialog.close(); });
    };

    triggers.forEach(trigger => {
        trigger.hidden = false;
        trigger.disabled = false;
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', () => {
            if (!dialog) prepare();
            if (dialog.open) return;
            activeTrigger = trigger;
            dialog.querySelector('[data-preview-price]').innerHTML = row.querySelector('[data-addon-price]').innerHTML;
            const selections = [...row.querySelectorAll('[data-addon-attribute]')].filter(select => select.value).map(select => select.selectedOptions[0].textContent);
            const variant = dialog.querySelector('[data-preview-variant]');
            variant.textContent = selections.length ? config.addonPreview.variant.replace('{variant}', selections.join(' · ')) : '';
            variant.hidden = !selections.length;
            const photos = [...dialog.querySelectorAll('[data-preview-photo]')];
            const match = photos.findIndex(photo => photo.dataset.previewVariants.split(' ').includes(String(getVariantId())));
            unlock = lockScroll();
            dialog.showModal();
            dialog.querySelector('.OvAddonPreview__body').scrollTop = 0;
            selectPhoto(Math.max(0, match), 0);
            triggers.forEach(button => button.setAttribute('aria-expanded', 'true'));
        });
    });
};
